import React, { useState, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight, FaSave } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useOfferWizard } from './OfferWizardContext';
import './OfferWizard.css';

const BASE_WIZARD_STEPS = [
    { id: 'tarifa', label: 'Tarifa', path: 'tarifas' },
    { id: 'cliente', label: 'Cliente', path: 'partner' },
    { id: 'pagador', label: 'Pagador', path: 'pagador' },
    { id: 'cups', label: 'CUPS', path: 'cups' },
    { id: 'documentacion', label: 'Documentación', path: 'documentacion' },
];

const OfferWizard = ({
    currentStep,
    ofertaId,
    sectorType,
    onStepChange,
    onSave,
    showVirtualBattery = false,
    blockedStages = [],
}) => {
    const navigate = useNavigate();
    const { actions } = useOfferWizard();
    const [localSaving, setLocalSaving] = useState(false);
    const actionInFlightRef = useRef(false);

    const getWizardSteps = () => {
        if (!showVirtualBattery) return BASE_WIZARD_STEPS;

        const steps = [...BASE_WIZARD_STEPS];
        const docIndex = steps.findIndex((s) => s.id === 'documentacion');
        steps.splice(docIndex, 0, {
            id: 'bateria-virtual',
            label: 'Batería Virtual',
            path: 'bateria-virtual',
        });
        return steps;
    };

    const WIZARD_STEPS = getWizardSteps();
    const currentStepIndex = WIZARD_STEPS.findIndex((step) => step.id === currentStep);
    const currentStepData = WIZARD_STEPS[currentStepIndex];
    const isSubmitting = actions.isSubmitting || localSaving;

    const isBlockedStage = (stepId) => blockedStages.includes(stepId);

    const getNextAvailableStep = (startIndex, direction) => {
        let idx = startIndex + direction;
        while (idx >= 0 && idx < WIZARD_STEPS.length) {
            const candidate = WIZARD_STEPS[idx];
            if (!isBlockedStage(candidate.id)) {
                return candidate;
            }
            idx += direction;
        }
        return null;
    };

    const navigateToStep = (step) => {
        if (!step || isBlockedStage(step.id)) {
            return;
        }

        if (step.path) {
            if (!ofertaId || ofertaId === 'null' || ofertaId === 'undefined') {
                console.warn('Attempted to navigate to step without valid ofertaId:', step.path);
                return;
            }
            const path = `/${sectorType}/${step.path}/${ofertaId}`;
            navigate(path);
        }

        if (onStepChange) {
            onStepChange(step.id);
        }
    };

    const handlePrevious = async () => {
        if (actions.onPrev) {
            await actions.onPrev();
            return;
        }

        if (currentStepIndex > 0) {
            const prevStep = getNextAvailableStep(currentStepIndex, -1);
            navigateToStep(prevStep);
        }
    };

    const handleNext = async () => {
        if (actionInFlightRef.current) return;
        actionInFlightRef.current = true;
        let shouldNavigate = true;
        if (actions.onNext) {
            setLocalSaving(true);
            try {
                const result = await actions.onNext();
                if (result && result.preventDefaultNavigation) {
                    shouldNavigate = false;
                }
            } catch (error) {
                console.error('Error in Next action:', error);
                shouldNavigate = false;
            } finally {
                setLocalSaving(false);
                actionInFlightRef.current = false;
            }
        } else {
            actionInFlightRef.current = false;
        }

        if (shouldNavigate && currentStepIndex !== -1) {
            const nextStep = getNextAvailableStep(currentStepIndex, 1);
            navigateToStep(nextStep);
        }
    };

    const handleSaveOnly = async () => {
        if (actionInFlightRef.current) return;
        actionInFlightRef.current = true;
        setLocalSaving(true);
        try {
            if (actions.onSave) {
                await actions.onSave();
            }
            if (onSave) {
                await onSave();
            }
        } catch (error) {
            console.error('Error saving:', error);
        } finally {
            setLocalSaving(false);
            actionInFlightRef.current = false;
        }
    };

    return (
        <div className="offer-wizard">
            <div className="wizard-navigation">
                <Button
                    variant="outline-secondary"
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                    className="nav-button"
                >
                    <FaArrowLeft className="me-2" />
                    Anterior
                </Button>

                <div className="wizard-step-info">
                    {currentStepIndex !== -1 && (
                        <span className="step-counter">
                            Paso {currentStepIndex + 1} de {WIZARD_STEPS.length}
                        </span>
                    )}
                    <h5 className="step-title mb-0">
                        {currentStepData
                            ? currentStepData.label
                            : (currentStep === 'bateria-virtual' ? 'Batería Virtual' : currentStep)}
                    </h5>
                </div>

                <Button
                    variant="outline-primary"
                    onClick={handleSaveOnly}
                    disabled={isSubmitting}
                    className="save-button"
                >
                    <FaSave className="me-2" />
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                </Button>

                <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="nav-button"
                >
                    {currentStepIndex === WIZARD_STEPS.length - 1 ? 'Finalizar' : 'Siguiente'}
                    <FaArrowRight className="ms-2" />
                </Button>
            </div>
        </div>
    );
};

export default OfferWizard;
